/**
 * The explicit actor context (AD-01).
 *
 * Four authorities exist and they never merge: `parent`, `child`, `device` and `operator`. The
 * shell holds which one is acting so that every surface reads it from one place instead of
 * inferring it from a route or a remembered preference.
 *
 * This module holds NO authorization. `actor` describes who the server said is acting; it never
 * decides what they may do. The server decides that on every request, and #28 (R6.3) proves it with
 * positive and negative cases.
 *
 * #26 (R6.1) wired the session. `resolveSession` is OPT-IN on purpose: without it this provider
 * behaves exactly as it did before and touches no network, so a component test stays a component
 * test. `App.jsx` supplies `fetchSession`, and only the running application reaches the server.
 *
 * `status` has four values, and the fourth is the one that matters:
 *   `loading`          — the answer is not back yet. Nothing may be decided from this.
 *   `authenticated`    — the server named an actor.
 *   `unauthenticated`  — the server said nobody is signed in. This is an ANSWER.
 *   `unavailable`      — the server could not answer. This is NOT "signed out". Treating a fault
 *                        as a sign-out would show the front door to a family that is logged in and
 *                        throw away where they were.
 *
 * #580 D1 (#898): `familyTurn` sits beside `status`, for a screen to READ: `null`, or the turn's
 * state from `toFamilyTurn` (`active`, `locked`, `revoked`, `unreadable`). A resolver, and
 * `setActor`, may hand over a whole session answer `{ actor, familyTurn }`; a plain actor or `null`
 * clears the turn state, so every existing caller keeps its meaning. An unreadable turn holds no actor
 * and reads `unavailable`, because the server's answer could not be read.
 */
import ProfileThemeBinding from './ProfileThemeBinding.jsx';
import { FeedbackProvider } from './FeedbackPreferences.jsx';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export const ACTOR_KINDS = Object.freeze(['parent', 'child', 'device', 'operator']);

const ActorContext = createContext(null);

/** A session answer (`{ actor, familyTurn }`) or a plain actor. An actor never has an `actor` key. */
function readAnswer(next) {
  if (next && typeof next === 'object' && 'actor' in next && 'familyTurn' in next) {
    return { actor: next.actor ?? null, familyTurn: next.familyTurn ?? null };
  }
  return { actor: next ?? null, familyTurn: null };
}

export function ActorProvider({ children, initialActor = null, resolveSession, feedbackPreferences, onSaveFeedbackPreferences, loadFeedbackPreferences, audioAssets, themeStore }) {
  const [actor, setActorState] = useState(initialActor);
  const [familyTurn, setFamilyTurn] = useState(null);
  // `null` means "no resolver, so the status comes from the actor alone" — the pre-#26 behavior.
  const [resolution, setResolution] = useState(resolveSession ? 'loading' : null);
  const resolverRef = useRef(resolveSession);
  resolverRef.current = resolveSession;

  /**
   * Every write to the actor bumps this. A resolve that started before the bump is STALE and its
   * answer is dropped (review H2).
   *
   * The failure without it: the initial `GET /api/auth/verify` is still in flight when the family
   * signs in. It finally answers 401 — which was true when it was asked and is false now — and
   * `setActor(null)` wipes the actor that sign-in just published, while a valid session cookie sits
   * in the jar. Nothing logs, and the family is shown the front door again.
   */
  const epochRef = useRef(0);

  const setActor = useCallback((next) => {
    epochRef.current += 1;
    const answer = readAnswer(next);
    setActorState(answer.actor);
    setFamilyTurn(answer.familyTurn);
    return epochRef.current;
  }, []);

  const refresh = useCallback(async ({ ifEpoch } = {}) => {
    // A delayed child-deactivate completion owns only the empty actor it left behind.
    if (ifEpoch !== undefined && epochRef.current !== ifEpoch) return null;
    const resolver = resolverRef.current;
    if (!resolver) return null;
    const startedAt = epochRef.current;
    setResolution('loading');
    try {
      const next = await resolver();
      const answer = readAnswer(next);
      if (epochRef.current !== startedAt) {
        // Superseded: someone published an actor while this request was in flight, so their answer
        // is the newer one. The status still settles, because the question "who is acting" now HAS
        // an answer — leaving it on `loading` would strand the shell.
        setResolution('resolved');
        return answer.actor;
      }
      setActor(next ?? null);
      setResolution(answer.familyTurn?.state === 'unreadable' ? 'unavailable' : 'resolved');
      return answer.actor;
    } catch {
      // The actor is deliberately LEFT ALONE (review M5). A fault is not evidence that the family
      // signed out, and clearing it here would also flip FeedbackProvider's identity key and
      // remount every route below this provider. The reason is not surfaced here either: a fault
      // message belongs to the surface that reports it.
      setResolution(epochRef.current === startedAt ? 'unavailable' : 'resolved');
      return null;
    }
  }, [setActor]);

  // Ask once. React 18 StrictMode mounts an effect twice in development, and a second session
  // request is a second server round trip for no answer, so the guard is a ref rather than a
  // dependency trick.
  const askedRef = useRef(false);
  useEffect(() => {
    if (!resolveSession || askedRef.current) return;
    askedRef.current = true;
    refresh();
  }, [resolveSession, refresh]);

  const value = useMemo(() => {
    // Precedence, and each rung is a different question:
    //   `unavailable` first, because "the last check FAILED" is a warning a surface must see even
    //     when it still holds an actor — otherwise a stale identity is trusted silently.
    //   then the actor, because once someone is known, a background re-check in flight does not
    //     make them unknown. Reporting `loading` there would strand a family who just signed in.
    //   then `loading`, which means only "nobody is known YET".
    let status;
    if (resolution === 'unavailable') status = 'unavailable';
    else if (actor) status = 'authenticated';
    else if (resolution === 'loading') status = 'loading';
    else status = 'unauthenticated';

    return {
      actor,
      status,
      familyTurn,
      kind: actor?.kind ?? null,
      isDevice: actor?.kind === 'device',
      setActor,
      refresh,
    };
  }, [actor, familyTurn, resolution, refresh, setActor]);

  // #577 (d-20260924-03). The feedback provider is keyed on `familyId`, and a session actor names
  // its household `tenantId` — without this the live app was silent AND could not save.
  const feedbackActor = useMemo(
    () => (actor && !actor.familyId && actor.tenantId ? { ...actor, familyId: actor.tenantId } : actor),
    [actor],
  );
  const feedbackIdentity = `${feedbackActor?.familyId || ''}:${feedbackActor?.id || ''}:${feedbackActor?.kind || ''}`;
  // d-20261001-04: the first answer, or an actor published before it, keeps the tree that rendered
  // while nobody was known, so a page (and a skip link's focus) survives. Every later identity change
  // remounts, even while the boot check is pending (P1-X). A tree rendered under a known actor
  // (`initialActor`) has its identity latched now, so a check that contradicts it remounts (review L6).
  const [firstIdentity, setFirstIdentity] = useState(resolveSession && !initialActor ? null : feedbackIdentity);
  const firstSettled = firstIdentity !== null || actor !== null || resolution !== 'loading';
  if (firstIdentity === null && firstSettled) setFirstIdentity(feedbackIdentity);
  const first = firstIdentity ?? (firstSettled ? feedbackIdentity : null);
  const sessionKey = first === null || feedbackIdentity === first ? 'first' : feedbackIdentity;
  // Reads started before or during a save must not overwrite its receipt. Each identity owns
  // its own counters, so a departing family's pending save cannot invalidate the next read.
  const feedbackRequests = useMemo(() => ({ epoch: 0, saving: 0, identity: feedbackIdentity }), [feedbackIdentity]);
  const saveFeedback = useCallback(async (...args) => {
    feedbackRequests.epoch += 1;
    feedbackRequests.saving += 1;
    try { return await onSaveFeedbackPreferences(...args); }
    finally { feedbackRequests.epoch += 1; feedbackRequests.saving -= 1; }
  }, [feedbackRequests, onSaveFeedbackPreferences]);
  const loaderRef = useRef(loadFeedbackPreferences);
  loaderRef.current = loadFeedbackPreferences;
  const [loaded, setLoaded] = useState({ identity: null, status: 'loading', preferences: null });
  const canLoad = !!loadFeedbackPreferences && !!feedbackActor?.familyId && feedbackActor.kind !== 'device';
  useEffect(() => {
    if (!canLoad) return undefined;
    let live = true;
    setLoaded({ identity: feedbackIdentity, status: 'loading', preferences: null });
    let latest = 0;
    const reload = () => {
      if (feedbackRequests.saving) return;
      const request = ++latest;
      const epoch = feedbackRequests.epoch;
      Promise.resolve()
        .then(() => loaderRef.current())
        .then(
          (preferences) => { if (live && request === latest && epoch === feedbackRequests.epoch) setLoaded({ identity: feedbackIdentity, status: preferences ? 'ready' : 'unavailable', preferences: preferences ? { ...preferences } : null }); },
          () => { if (live && request === latest && epoch === feedbackRequests.epoch) setLoaded({ identity: feedbackIdentity, status: 'unavailable', preferences: null }); },
        );
    };
    reload();
    window.addEventListener('focus', reload);
    const timer = window.setInterval(reload, 30_000);
    return () => {
      live = false;
      window.removeEventListener('focus', reload);
      window.clearInterval(timer);
    };
  }, [canLoad, feedbackIdentity, feedbackRequests]);
  // Without a loader (a component test) the host's own `feedbackPreferences` stand, as before.
  const availability = !canLoad ? 'ready' : loaded.identity === feedbackIdentity ? loaded.status : 'loading';
  const initialPreferences = canLoad ? loaded.preferences : feedbackPreferences;

  return <ActorContext.Provider value={value}><FeedbackProvider sessionKey={sessionKey} actor={feedbackActor} initialPreferences={initialPreferences}
    availability={availability} onSave={onSaveFeedbackPreferences ? saveFeedback : undefined} assets={audioAssets}><ProfileThemeBinding profileId={['parent', 'child'].includes(actor?.kind) ? actor.id : null} store={themeStore}>
      {children}</ProfileThemeBinding></FeedbackProvider></ActorContext.Provider>;
}

/**
 * The actor context, or `null` outside an `<ActorProvider>`. For a screen that only needs to KNOW
 * who is signed in and must still render in isolation (the Family screen hides "Deactivate" on the
 * parent's own card, #577 row 7). A write never trusts this: the server refuses it anyway.
 */
export function useOptionalActor() {
  return useContext(ActorContext);
}

export function useActor() {
  const value = useContext(ActorContext);
  if (!value) throw new Error('useActor must be used inside <ActorProvider>');
  return value;
}
